import { action, IReactionDisposer, reaction, toJS } from 'mobx'
import {
  IApiServicesStateFacadeDI,
  IApiServicesStateReactionFacade,
  IAPIServiceStateFacade,
  IStateViewModel,
  DebounceOptions,
} from '../mobxUtils.types'

export class ApiServicesStateReactionFacade<
  IServiceState extends Record<
    keyof IServiceState,
    IAPIServiceStateFacade<any, any>
  >,
  IParams extends Record<keyof IParams, IStateViewModel<any>>,
> implements IApiServicesStateReactionFacade<IServiceState, IParams>
{
  private readonly apiServiceStates: IServiceState
  private readonly apiParams: IParams
  private reactionDisposers: Array<IReactionDisposer> = []
  private debouncedFunctions: Map<string, NodeJS.Timeout> = new Map()

  constructor({
    apiServiceStates,
    apiParams,
  }: IApiServicesStateFacadeDI<IServiceState, IParams>) {
    this.apiServiceStates = apiServiceStates
    this.apiParams = apiParams
  }

  private createDebouncedFunction = <K extends keyof IServiceState>(
    serviceKey: K,
    apiServiceState: IServiceState[K],
    directApiParams: any[] | undefined,
    delay: number
  ) => {
    return (apiParams: any) => {
      const timeoutKey = String(serviceKey)
      const existingTimeout = this.debouncedFunctions.get(timeoutKey)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
      }

      const timeout = setTimeout(() => {
        try {
          if (directApiParams) {
            apiServiceState.request(...directApiParams, apiParams)
          } else {
            apiServiceState.request(apiParams)
          }
        } catch (error) {
          console.error(error)
        }
        this.debouncedFunctions.delete(timeoutKey)
      }, delay)

      this.debouncedFunctions.set(timeoutKey, timeout)
    }
  }

  public initDataLoadingReaction = <K extends keyof IServiceState>(
    serviceKey: K,
    directApiParams?: any[],
    debounceOptions?: DebounceOptions
  ) => {
    const apiServiceState = this.apiServiceStates[serviceKey]

    const requestFunction = debounceOptions?.enabled
      ? this.createDebouncedFunction(
          serviceKey,
          apiServiceState,
          directApiParams,
          debounceOptions.delay
        )
      : (apiParams: any) => {
          try {
            if (directApiParams) {
              apiServiceState.request(...directApiParams, apiParams)
            } else {
              apiServiceState.request(apiParams)
            }
          } catch (error) {
            console.error(error)
          }
        }

    const disposer = reaction(
      () => {
        if (this.apiParams && serviceKey in this.apiParams) {
          return toJS(
            this.apiParams[serviceKey as unknown as keyof IParams].state
          )
        }
        return undefined
      },
      requestFunction,
      { fireImmediately: true }
    )

    this.reactionDisposers.push(disposer)
  }

  public requestService = async <K extends keyof IServiceState>(
    serviceKey: K,
    directApiParams?: any[]
  ): Promise<Awaited<ReturnType<IServiceState[K]['request']>>> => {
    const apiServiceState = this.apiServiceStates[serviceKey]
    let apiParams = undefined
    if (this.apiParams && serviceKey in this.apiParams) {
      apiParams = this.apiParams[serviceKey as unknown as keyof IParams].state
    }
    try {
      if (directApiParams) {
        return await apiServiceState.request(...directApiParams, apiParams)
      } else {
        return await apiServiceState.request(apiParams)
      }
    } catch (error) {
      console.error(error)
      throw new Error(error)
    }
  }

  public getStateByService = <K extends keyof IServiceState>(
    key: K
  ): IServiceState[K]['stateViewModel']['state'] => {
    return this.apiServiceStates[key].stateViewModel.state
  }

  public updateStateByService = <K extends keyof IServiceState>(
    key: K
  ): IServiceState[K]['stateViewModel']['setState'] => {
    return this.apiServiceStates[key].stateViewModel.setState
  }

  public getStateStatusByService = <K extends keyof IServiceState>(
    key: K
  ): IServiceState[K]['stateViewModel']['status'] => {
    return this.apiServiceStates[key].stateViewModel.status
  }

  public getIsFirstLoadingByService = <K extends keyof IServiceState>(
    key: K
  ): IServiceState[K]['stateViewModel']['isFirstLoading'] => {
    return this.apiServiceStates[key].stateViewModel.isFirstLoading
  }

  public getStateErrorByService = <K extends keyof IServiceState>(
    key: K
  ): IServiceState[K]['stateViewModel']['error'] => {
    return this.apiServiceStates[key].stateViewModel.error
  }

  public getApiParamsStateByService = <K extends keyof IParams>(
    key: K
  ): IParams[K]['state'] => {
    return this.apiParams[key].state
  }

  public getApiParamsInitialStateByService = <K extends keyof IParams>(
    key: K
  ): IParams[K]['initialState'] => {
    return this.apiParams[key].initialState
  }

  public getApiParamsDefaultStateByService = <K extends keyof IParams>(
    key: K
  ): IParams[K]['defaultState'] => {
    return this.apiParams[key].defaultState
  }

  public initApiParamsStateByService = <T extends keyof IParams>(key: T) => {
    return (params: Parameters<IParams[T]['initState']>[0]) =>
      this.apiParams[key].initState(params)
  }

  public initDefaultApiParamsStateByService = <T extends keyof IParams>(
    key: T
  ) => {
    return (params: Parameters<IParams[T]['initDefaultState']>[0]) =>
      this.apiParams[key].initDefaultState(params)
  }

  public updateApiParamsStateByService = <T extends keyof IParams>(key: T) => {
    return (params: Parameters<IParams[T]['updateState']>[0]) => {
      return this.apiParams[key].updateState(params)
    }
  }

  public setApiParamsStateByService = <T extends keyof IParams>(key: T) => {
    return (params: Partial<Parameters<IParams[T]['setState']>[0]>) => {
      return this.apiParams[key].setState(params)
    }
  }

  public resetApiParamsStateByService = <T extends keyof IParams>(key: T) => {
    console.log('resetApiParamsStateByService', key)
    this.apiParams[key].resetState()
  }

  public injectRequestMethodByService = <K extends keyof IServiceState>(
    key: K
  ): IServiceState[K]['injectRequestMethod'] => {
    return this.apiServiceStates[key].injectRequestMethod
  }

  @action
  public clearData = () => {
    Object.keys(this.apiServiceStates).forEach((key) => {
      this.apiServiceStates[key].cancelPendingRequests()
      this.apiServiceStates[key].stateViewModel.clearData()
    })
    Object.keys(this.apiParams).forEach((key) => {
      this.apiParams[key].clearState()
    })
  }

  @action
  public clear = () => {
    this.reactionDisposers.forEach((disposer) => disposer())
    this.reactionDisposers = []

    this.debouncedFunctions.forEach((timeout) => clearTimeout(timeout))
    this.debouncedFunctions.clear()

    this.clearData()
  }
}
